import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      window.dispatchEvent(new Event('tracern:unauthorized'));
    }
    if (!err.response) {
      // Network error — backend unreachable
      window.dispatchEvent(new CustomEvent('tracern:network-error', {
        detail: { message: err.message }
      }));
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  login:         (employeeId, password) => api.post('/auth/login', { employeeId, password }),
  logout:        ()                     => api.post('/auth/logout'),
  me:            ()                     => api.get('/auth/me'),
  resetPassword: (newPassword, confirmPassword) =>
                   api.post('/auth/reset-password', { newPassword, confirmPassword }),
};

export const notificationApi = {
  getAll:         () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAllRead:    () => api.post('/notifications/read-all'),
};

export const dashboardApi = {
  superAdmin: () => api.get('/dashboard/super-admin'),
  admin:      () => api.get('/dashboard/admin'),
  employee:   () => api.get('/dashboard/employee'),
};

export default api;

export const departmentApi = {
  getAll:  ()             => api.get('/departments'),
  create:  (name)         => api.post('/departments', { name }),
  delete:  (id)           => api.delete(`/departments/${id}`),
};

export const userApi = {
  getAll:            ()             => api.get('/users'),
  getById:           (id)           => api.get(`/users/${id}`),
  create:            (data)         => api.post('/users', data),
  update:            (id, data)     => api.put(`/users/${id}`, data),
  resetPassword:     (id)           => api.post(`/users/${id}/reset-password`),
  deactivate:        (id)           => api.post(`/users/${id}/deactivate`),
  activate:          (id)           => api.post(`/users/${id}/activate`),
  assignDepartments: (id, deptIds)  => api.post(`/users/${id}/assign-departments`, { departmentIds: deptIds }),
};

export const settingsApiFull = {
  get:    ()       => api.get('/settings'),
  update: (data)   => api.put('/settings', data),
};

export const taskApi = {
  getAll:            ()              => api.get('/tasks'),
  getMyTasks:        ()              => api.get('/tasks/my'),
  getPool:           ()              => api.get('/tasks/pool'),
  getOverdue:        ()              => api.get('/tasks/overdue'),
  getCritical:       ()              => api.get('/tasks/critical'),
  getById:           (id)            => api.get(`/tasks/${id}`),
  getHistory:        (id)            => api.get(`/tasks/${id}/history`),
  create:            (data)          => api.post('/tasks', data),
  update:            (id, data)      => api.put(`/tasks/${id}`, data),
  updateStatus:      (id, data)      => api.post(`/tasks/${id}/status`, data),
  delete:            (id)            => api.delete(`/tasks/${id}`),
  duplicate:         (id)            => api.post(`/tasks/${id}/duplicate`),
  claim:             (id)            => api.post(`/tasks/${id}/claim`),
  requestExtension:  (id, data)      => api.post(`/tasks/${id}/extension-request`, data),
  respondExtension:  (id, data)      => api.post(`/tasks/${id}/extension-response`, data),
  getNotes:          (id)            => api.get(`/tasks/${id}/notes`),
  addNote:           (id, data)      => api.post(`/tasks/${id}/notes`, data),
};

export const teamApi = {
  getTeam: () => api.get('/team'),
};

export const schedulerApi = {
  runOverdue:       () => api.post('/scheduler/run-overdue'),
  runDigest:        () => api.post('/scheduler/run-digest'),
  runOnHold:        () => api.post('/scheduler/run-onhold'),
  runCompanyStatus: () => api.post('/scheduler/run-company-status'),
  runReminders:     () => api.post('/scheduler/run-reminders'),
};

export const adminApi = {
  getDashboard:        (deptId) => api.get('/admin/dashboard', { params: deptId ? { deptId } : {} }),
  getTasks:            (deptId) => api.get('/admin/tasks',     { params: deptId ? { deptId } : {} }),
  getExtensionRequests:()       => api.get('/admin/extension-requests'),
};

export const logApi = {
  getActivity:       ()       => api.get('/logs/activity'),
  getAdminActions:   ()       => api.get('/logs/admin-actions'),
  reverseAction:     (id, note) => api.post(`/logs/admin-actions/${id}/reverse`, { reversalNote: note }),
};

export const employeeManagementApi = {
  getProfile:              (id)                => api.get(`/employee-management/${id}/profile`),
  markAway:                (id, awayReturnDate)=> api.post(`/employee-management/${id}/away`, { awayReturnDate }),
  markBack:                (id)                => api.post(`/employee-management/${id}/back`),
  requestDeletion:         (id, reason)        => api.post(`/employee-management/${id}/deletion-request`, { reason }),
  getPendingDeletions:     ()                  => api.get('/employee-management/deletion-requests/pending'),
  getAllDeletionRequests:   ()                  => api.get('/employee-management/deletion-requests'),
  respondToDeletion:       (reqId, decision, responseNote) =>
                             api.post(`/employee-management/deletion-requests/${reqId}/respond`, { decision, responseNote }),
};

export const reportApi = {
  getEmployeeReport: (userId, from, to) =>
    api.get(`/reports/employee/${userId}`, { params: { from, to } }),
  exportEmployeeReport: (userId, format, from, to) =>
    api.get(`/reports/employee/${userId}/export`,
      { params: { format, from, to }, responseType: 'blob' }),
  getOverdueReport: (from, to) =>
    api.get('/reports/overdue', { params: { from, to } }),
  exportOverdueReport: (format, from, to) =>
    api.get('/reports/overdue/export',
      { params: { format, from, to }, responseType: 'blob' }),
};

export const bulkApi = {
  downloadTemplate: () =>
    api.get('/employees/bulk/template', { responseType: 'blob' }),
  upload: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/employees/bulk/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const taskHandoffApi = {
  selfUnassign: (id, reason)          => api.post(`/tasks/${id}/self-unassign`, { reason }),
  handoff:      (id, handoffToId, reason) =>
                  api.post(`/tasks/${id}/handoff`, { handoffToId, reason }),
};

// ── God Mode API — completely separate from tenant API ────────────────────────
const godApi = axios.create({
  baseURL: '/god',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

godApi.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      window.dispatchEvent(new Event('tracern:god-unauthorized'));
    }
    return Promise.reject(err);
  }
);

export const godAuthApi = {
  login:  (username, password) => godApi.post('/auth/login', { employeeId: username, password }),
  logout: ()                   => godApi.post('/auth/logout'),
  me:     ()                   => godApi.get('/auth/me'),
};

export { godApi };

export const godCompanyApi = {
  list:              (params)    => godApi.get('/companies', { params }),
  dashboardCounts:   ()          => godApi.get('/companies/dashboard-counts'),
  getById:           (id)        => godApi.get(`/companies/${id}`),
  create:            (data)      => godApi.post('/companies', data),
  update:            (id, data)  => godApi.put(`/companies/${id}`, data),
  suspend:           (id, reason)=> godApi.post(`/companies/${id}/suspend`, { reason }),
  reactivate:        (id)        => godApi.post(`/companies/${id}/reactivate`),
  resetSuperAdmin:   (id)        => godApi.post(`/companies/${id}/reset-super-admin`),
  resetSaMfa:        (companyId, employeeId) => godApi.post(`/companies/${companyId}/users/${employeeId}/reset-mfa`),
};

export const godAuditApi = {
  getAll:       (params)     => godApi.get('/audit-log', { params }),
  getByCompany: (companyId, params) => godApi.get(`/audit-log/company/${companyId}`, { params }),
};

export const godMetricsApi = {
  dashboard:      ()   => godApi.get('/metrics/dashboard'),
  companyMetrics: (id) => godApi.get(`/metrics/company/${id}`),
  recentSignups:  (n)  => godApi.get('/metrics/recent-signups', { params: { limit: n } }),
};

export const planApi = {
  getUsage: () => api.get('/plan/usage'),
};

export const tosApi = {
  getCurrent: () => api.get('/tos/current'),
  accept:     () => api.post('/tos/accept'),
};

export const exportApi = {
  downloadMyData: () =>
    api.get('/export/company-data', { responseType: 'blob' }),
};

export const godOffboardingApi = {
  exportCompany:    (id) =>
    godApi.get(`/offboarding/export/${id}`, { responseType: 'blob' }),
  churnCompany:     (id) =>
    godApi.post(`/offboarding/churn/${id}`),
  deleteCompany:    (id, confirmedName) =>
    godApi.delete(`/offboarding/delete/${id}`, { data: { confirmedName } }),
  retentionReview:  () =>
    godApi.get('/offboarding/retention-review'),
};

export const personalNoteApi = {
  get:   ()        => api.get('/personal-note'),
  save:  (content) => api.put('/personal-note', { content }),
  clear: ()        => api.delete('/personal-note'),
};

export const securityAuditApi = {
  getLogs: (params) => api.get('/security-audit', { params }),
};

// ── Holiday API ────────────────────────────────────────────────────────────────
export const holidayApi = {
  getHolidays:            ()           => api.get('/holidays'),
  addHoliday:             (data)       => api.post('/holidays', data),
  deleteHoliday:          (id)         => api.delete(`/holidays/${id}`),
  updateConfig:           (data)       => api.put('/holidays/config', data),
  getMySelection:         ()           => api.get('/holidays/my-selection'),
  saveSelection:          (data)       => api.post('/holidays/my-selection', data),
  requestChange:          (data)       => api.post('/holidays/my-selection/request-change', data),
  respondToChange:        (id, approve)=> api.post(`/holidays/change-requests/${id}/respond?approve=${approve}`),
  getOnLeaveToday:        ()           => api.get('/holidays/on-leave-today'),
  getChangeRequests:      ()           => api.get('/holidays/change-requests'),
};

// ── Announcements API ──────────────────────────────────────────────────────────
export const announcementApi = {
  getAll:        (departmentId) => api.get('/announcements' + (departmentId ? `/filter?departmentId=${departmentId}` : '')),
  getCompanyWide:()             => api.get('/announcements/filter'),  // no departmentId = company-wide only
  create:      (data)         => api.post('/announcements', data),
  archive:     (id)           => api.delete(`/announcements/${id}`),
  togglePin:   (id)           => api.put(`/announcements/${id}/pin`),
};

// ── Reminders API ──────────────────────────────────────────────────────────────
export const reminderApi = {
  getActive:   ()       => api.get('/reminders'),
  getAll:      ()       => api.get('/reminders/all'),
  create:      (data)   => api.post('/reminders', data),
  update:      (id, data) => api.put(`/reminders/${id}`, data),
  dismiss:     (id)     => api.post(`/reminders/${id}/dismiss`),
  delete:      (id)     => api.delete(`/reminders/${id}`),
};

// ── Bulk Task Upload API ────────────────────────────────────────────────────────
export const bulkTaskApi = {
  downloadTemplate: () =>
    api.get('/tasks/bulk/template', { responseType: 'blob' }),
  upload: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/tasks/bulk/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ── MFA API ────────────────────────────────────────────────────────────────────
export const mfaApi = {
  status:        ()           => api.get('/auth/mfa/status'),
  setup:         ()           => api.post('/auth/mfa/setup'),
  confirmSetup:  (code)       => api.post('/auth/mfa/setup/confirm', { code }),
  verify:        (tempToken, code) => api.post('/auth/mfa/verify', { tempToken, code }),
  disable:       (code)       => api.post('/auth/mfa/disable', { code }),
  adminReset:    (userId)     => api.post(`/users/${userId}/reset-mfa`),
};
